import { useInfiniteQuery, useQuery } from 'react-query';
import { sharedCompanyService } from 'api/services';
import { CACHE_TIME_LONG } from 'shared/react-query/query-defaults';
import { QUERY_KEYS } from 'api/query-keys';
import { companiesResponse2CompaniesSelectModel } from 'hq/services/mappers/mappers';
import { useSiteDetection } from 'shared/hooks';
import { DEFAULT_PAGE_SIZE } from 'shared/utils/constants';

type Response = Awaited<ReturnType<typeof sharedCompanyService.getCompanies>>;

const useGetCompanies = <TData = Response>(
  filter?: string,
  includeArchived: boolean = false,
  select?: (data: Response) => TData
) => {
  const { site } = useSiteDetection();
  const queryFn = async () =>
    await sharedCompanyService.getCompanies({ site, includeArchived, filter });

  return useQuery(QUERY_KEYS.companiesList(filter), queryFn, {
    select,
    cacheTime: CACHE_TIME_LONG,
  });
};

const useGetCompaniesInfinite = (filter?: string, includeArchived: boolean = false) => {
  const { site } = useSiteDetection();

  return useInfiniteQuery({
    queryKey: QUERY_KEYS.companiesListPaginated(filter),
    queryFn: async ({ pageParam = 0 }) =>
      await sharedCompanyService.getCompaniesPage({
        site,
        includeArchived,
        filter,
        page: pageParam,
        pageSize: DEFAULT_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.nextPage : undefined),
    cacheTime: CACHE_TIME_LONG,
  });
};

const useGetCompaniesAsSelect = (filter?: string, includeArchived?: boolean) => {
  const result = useGetCompanies(filter, includeArchived, companiesResponse2CompaniesSelectModel);
  return result;
};

export { useGetCompanies, useGetCompaniesAsSelect, useGetCompaniesInfinite };
